import {
  cardBrandLabels,
  cardTypeLabels,
  klarnaBillingCategory,
  paymentMethodLabels,
  threeDSecureFlowStatus,
  threeDSecureStatus,
  tokenizationMethodLabels
} from '../lib/labels';
import {formatEmail, formatPhone} from '../lib/formaters';
import {BillingCategoryKlarna, Maybe, PaymentMethod, PaymentMethods} from '../api/generated';
import {CheckOutlined, CloseOutlined} from '@ant-design/icons';
import {colors} from '../lib/colors';
import {Hint} from './Hint';
import {PropertyList} from './PropertyList';
import React from 'react';
import {useCountries} from '../hooks/useCountries';
import ReactCountryFlag from 'react-country-flag';
import {ThreeDSecureFlowHint} from './ThreeDSecureFlowHint';

export const PaymentMethodBlock: React.FC<{
  paymentMethod: PaymentMethod;
  countryCode: Maybe<string | undefined>;
}> = ({paymentMethod, countryCode}) => {

  const {getCountryName} = useCountries();
  const getCountry = (countryCode?: string | null) => {
    if (!countryCode) return null;
    return (
      <>
        <ReactCountryFlag countryCode={countryCode} svg style={{marginRight: 5, marginBottom: 3}} />
        {getCountryName(countryCode)}
      </>
    );
  };

  return (
    <PropertyList
      omitEmpty
      properties={[
        {
          label: 'Method',
          value: paymentMethodLabels[paymentMethod.method!]
        },
        {
          label: 'Digital wallet',
          value: tokenizationMethodLabels[paymentMethod?.card?.tokenizationMethod!]
        },
        {
          label: 'Integration type',
          value: paymentMethod?.bizum?.integrationType
        },
        {
          label: 'Phone number',
          value: formatPhone(paymentMethod?.bizum?.phoneNumber, countryCode)
        },
        {
          label: 'Cardholder email',
          value: formatEmail(paymentMethod.card?.cardholderEmail || paymentMethod.paypal?.email || paymentMethod.cardPresent?.cardholderEmail)
        },
        {
          label: 'Cardholder name',
          value: paymentMethod.card?.cardholderName || paymentMethod.paypal?.name || paymentMethod.cardPresent?.cardholderName
        },
        {
          label: 'PayPal order ID',
          value: paymentMethod.paypal?.orderId
        },
        {
          label: 'PayPal customer ID',
          value: paymentMethod.paypal?.payerId
        },
        {
          label: 'Brand',
          value: paymentMethod.card?.brand ? cardBrandLabels[paymentMethod.card.brand] : paymentMethod.cardPresent?.brand ? cardBrandLabels[paymentMethod.cardPresent.brand] : null
        },
        {
          label: 'Type',
          value: paymentMethod.card?.type ? cardTypeLabels[paymentMethod.card.type] : paymentMethod.cardPresent?.type ? cardTypeLabels[paymentMethod.cardPresent.type] : null
        },
        {
          label: 'Number',
          value: paymentMethod.card?.last4 ? `•••• ${paymentMethod.card.last4}` : paymentMethod.cardPresent?.last4 ? `•••• ${paymentMethod.cardPresent.last4}` : null
        },
        {
          label: '3D Secure',
          value:
            paymentMethod.method === PaymentMethods.CARD ? (
              paymentMethod.card?.threeDSecure ? (
                <>
                  <CheckOutlined style={{color: colors.green}} />{' '}
                  {paymentMethod.card?.threeDSecureVersion}
                </>
              ) : (
                <CloseOutlined style={{color: colors.red}} />
              )
            ) : null
        },
        {
          label: '3D Secure flow',
          value: paymentMethod.card?.threeDSecureFlow ? (
            <>
              <Hint
                title={threeDSecureFlowStatus[paymentMethod.card.threeDSecureFlow]}
                message={
                  <ThreeDSecureFlowHint threeDSecureFlow={paymentMethod.card.threeDSecureFlow} />
                }
              />
            </>
          ) : null
        },
        {
          label: '3D Secure status',
          value: paymentMethod.card?.threeDSecureStatus
            ? threeDSecureStatus[paymentMethod.card.threeDSecureStatus]
            : null
        },
        {label: 'Country', value: getCountry(countryCode)},
        {
          label: 'Bank',
          value: paymentMethod.card?.bank
        },
        {
          label: 'Billing category',
          value:
            klarnaBillingCategory[paymentMethod.klarna?.billingCategory as BillingCategoryKlarna]
        }
      ]}
    />
  );
};
